import SearchSvg from "@/src/assets/icons/icon-search.svg";
import { AddressDto } from "@/src/store/apis/common/interface";
import colorSet from "@/src/styles/color";
import typo from "@/src/styles/typography";
import { filterEnglishOnly } from "@/src/utils/inputFilter";
import { ReactNode, useRef, useState } from "react";
import {
  FieldValues,
  Path,
  PathValue,
  UseFormClearErrors,
  UseFormRegister,
  UseFormSetValue,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { styled } from "styled-components";
import { Button } from "../../atom/Button";
import Icon from "../../atom/Icon";
import Input from "../../atom/Input";
import Typo from "../../atom/Typo";
import AddressSearchModal from "../../organism/AddressSearchModal";
import { InputError } from "../FormItem";

interface AddressFormItemProps<TFieldValues extends FieldValues> {
  register: UseFormRegister<TFieldValues>;
  isRequired?: boolean;
  label?: ReactNode;
  setValue?: UseFormSetValue<TFieldValues>;
  direction?: "horizontal" | "vertical";
  errorMessage?: ReactNode;
  className?: string;
  error?: any;
  clearErrors?: UseFormClearErrors<any>;
  onAddressItemClick?: (address: AddressDto, normalizedCountryName?: string) => void;
  disabled?: boolean;
  englishOnly?: boolean;
  apartmentValue?: string;
  cityValue?: string;
  stateValue?: string;
  postalValue?: string;
  countryValue?: string;
}

const AddressFormItem = <TFieldValues extends FieldValues>({
  register,
  isRequired = true,
  label = "Address",
  setValue,
  direction = "vertical",
  errorMessage,
  className,
  error,
  clearErrors,
  onAddressItemClick,
  disabled,
  englishOnly = false,
  apartmentValue,
  cityValue,
  stateValue,
  postalValue,
  countryValue,
}: AddressFormItemProps<TFieldValues>) => {
  const { t } = useTranslation();
  const [isSearchDialogOpen, setIsSearchDialogOpen] = useState<boolean>(false);
  const isStringLabel = typeof label === "string";
  const apartmentInputRef = useRef<HTMLInputElement | null>(null);
  const handleSearchClick = () => {
    setIsSearchDialogOpen(true);
  };

  const { ref, ...apartmentRestField } = register(
    "apartment" as Path<TFieldValues>,
    { required: false },
  );

  const setHookFormValue = (
    name: Path<TFieldValues>,
    value: PathValue<TFieldValues, Path<TFieldValues>>,
  ) => {
    setValue?.(name, value);
  };

  const createChangeHandler =
    (fieldName: Path<TFieldValues>) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = englishOnly
        ? filterEnglishOnly(e.target.value)
        : e.target.value;
      setHookFormValue(
        fieldName,
        value as PathValue<TFieldValues, Path<TFieldValues>>,
      );
    };

  const createClearHandler = (fieldName: Path<TFieldValues>) => () => {
    setHookFormValue(
      fieldName,
      "" as PathValue<TFieldValues, Path<TFieldValues>>,
    );
  };

  const renderDialog = () => {
    if (!isSearchDialogOpen) return null;
    return (
      <AddressSearchModal
        onClose={() => setIsSearchDialogOpen(false)}
        onAddressItemClick={(addressDetail) => {
          if (addressDetail) {
            const normalizedCountryName = addressDetail.countryName?.replace(
              "South Korea",
              "Korea",
            );
            onAddressItemClick?.(addressDetail, normalizedCountryName);
            setHookFormValue(
              "apartment" as Path<TFieldValues>,
              (addressDetail?.streetAddress ?? "") as PathValue<
                TFieldValues,
                Path<TFieldValues>
              >,
            );
            setHookFormValue(
              "city" as Path<TFieldValues>,
              (addressDetail?.locality ?? "") as PathValue<
                TFieldValues,
                Path<TFieldValues>
              >,
            );
            setHookFormValue(
              "state" as Path<TFieldValues>,
              (addressDetail?.region ?? "") as PathValue<
                TFieldValues,
                Path<TFieldValues>
              >,
            );
            setHookFormValue(
              "postal" as Path<TFieldValues>,
              (addressDetail?.postalCode ?? "") as PathValue<
                TFieldValues,
                Path<TFieldValues>
              >,
            );
            setHookFormValue(
              "country" as Path<TFieldValues>,
              (addressDetail?.countryName ?? "") as PathValue<
                TFieldValues,
                Path<TFieldValues>
              >,
            );
            if (addressDetail?.locality) {
              clearErrors?.("city");
            }
            if (addressDetail?.region) {
              clearErrors?.("state");
            }
            if (addressDetail?.postalCode) {
              clearErrors?.("postal");
            }
            if (addressDetail?.countryName) {
              clearErrors?.("country");
            }
            setIsSearchDialogOpen(false);
            apartmentInputRef.current?.focus();
          }
        }}
      />
    );
  };

  return (
    <FormItemContainer data-direction={direction} className={className}>
      {isStringLabel ? (
        <LabelContainer>
          <FormLabel data-direction={direction} data-required={isRequired}>
            {label}
          </FormLabel>
          <div>
            <StyledButton
              buttonSize={24}
              buttonGrade="tertiary"
              buttonColor="black"
              onClick={handleSearchClick}
              disabled={disabled}
            >
              <StyledIcon iconSrc={SearchSvg} iconSize={16} />
              <Typo typoType="btn4m" color="gray2">
                {t("signup:v2.searchAddress")}
              </Typo>
            </StyledButton>
          </div>
        </LabelContainer>
      ) : (
        label
      )}
      <AddressFormItemContainer>
        <StyledInput
          ref={(node) => {
            ref(node);
            apartmentInputRef.current = node;
          }}
          {...apartmentRestField}
          value={apartmentValue ?? ""}
          disabled={disabled}
          placeholder={`${t(
            "signup:v2.address.placeholder.apartmentUnit",
          )} ${isRequired ? `(${t("common:optional")})` : ""}`}
          onChange={createChangeHandler("apartment" as Path<TFieldValues>)}
          onClear={createClearHandler("apartment" as Path<TFieldValues>)}
        />
        <StyledInput
          {...register("city" as Path<TFieldValues>)}
          value={cityValue ?? ""}
          placeholder={`${t("signup:v2.address.placeholder.city")} ${
            isRequired ? `(${t("common:optional")})` : ""
          }`}
          disabled={disabled}
          onChange={createChangeHandler("city" as Path<TFieldValues>)}
          onClear={createClearHandler("city" as Path<TFieldValues>)}
        />
        <HalfInputsContainer>
          <FlexColumn>
            <HalfInput
              {...register("state" as Path<TFieldValues>)}
              value={stateValue ?? ""}
              placeholder={`${t(
                "signup:v2.address.placeholder.state",
              )} ${isRequired ? `(${t("common:optional")})` : ""}`}
              disabled={disabled}
              onChange={createChangeHandler("state" as Path<TFieldValues>)}
              onClear={createClearHandler("state" as Path<TFieldValues>)}
            />
          </FlexColumn>
          <FlexColumn>
            <HalfInput
              {...register("postal" as Path<TFieldValues>)}
              value={postalValue ?? ""}
              placeholder={`${t(
                "signup:v2.address.placeholder.postalCode",
              )} ${isRequired ? `(${t("common:optional")})` : ""}`}
              disabled={disabled}
              onChange={createChangeHandler("postal" as Path<TFieldValues>)}
              onClear={createClearHandler("postal" as Path<TFieldValues>)}
            />
          </FlexColumn>
        </HalfInputsContainer>
        <StyledInput
          {...register("country" as Path<TFieldValues>, {
            required: isRequired,
          })}
          value={countryValue ?? ""}
          placeholder={t("signup:v2.address.placeholder.country")}
          data-invalid={!!error?.["country"]}
          disabled={disabled}
          onChange={createChangeHandler("country" as Path<TFieldValues>)}
          onClear={createClearHandler("country" as Path<TFieldValues>)}
        />
        {error?.["country"] && (
          <InputError message={errorMessage || t("error:required")} />
        )}
      </AddressFormItemContainer>
      {renderDialog()}
    </FormItemContainer>
  );
};

export default AddressFormItem;

export const AddressFormItemContainer = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 8px;
`;

const HalfInputsContainer = styled.div`
  display: flex;
  width: 100%;
  gap: 8px;
  flex-direction: row;
`;

const StyledInput = styled(Input)`
  width: 100%;
`;

const HalfInput = styled(Input)`
  width: 100%;
`;

const FormLabel = styled.p`
  ${typo.b7r};
  flex-shrink: 0;
  min-width: 163px;
  max-width: 163px;

  &[data-required="true"] {
    &::after {
      content: " *";
      color: ${colorSet.red2};
    }
  }
`;

const FormItemContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  &[data-direction="vertical"] {
    ${AddressFormItemContainer} {
      width: calc(100% - 163px);
      margin-left: auto;
    }
  }
`;

const StyledIcon = styled(Icon)``;

const StyledButton = styled(Button)`
  display: flex;
  gap: 2px;
  margin-left: auto;

  ${StyledIcon} {
    flex-shrink: 0;
  }
`;

const LabelContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

const FlexColumn = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 8px;
`;
